import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Mail, Phone, Briefcase, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  useGetUserGroupsQuery,
  useGetUserPermissionsQuery,
} from "../../infrastructure/api/membersGroupsApi";
import type { Group, Member } from "../../domain/entities/Member";
import { ViewGroupMembersDialog } from "./ViewGroupMembersDialog";

interface Props {
  open: boolean;
  onOpenChange: (val: boolean) => void;
  member: Member | null;
}

export const ViewMemberDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  member,
}) => {
  const { t } = useTranslation("membersGroups");
  const { data: userGroups = [], isLoading: loadingGroups } =
    useGetUserGroupsQuery(member?.id ?? "", {
      skip: !open || !member,
      refetchOnMountOrArgChange: true,
    });

  const { data: permissionsData, isLoading: loadingPerms } =
    useGetUserPermissionsQuery(member?.id ?? "", {
      skip: !open || !member,
      refetchOnMountOrArgChange: true,
    });

  const groupedPermissions = permissionsData?.permissionGroups ?? [];

  const [viewGroup, setViewGroup] = useState<Group | null>(null);

  if (!member) return null;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[calc(100dvh-1rem)] max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:h-auto sm:max-h-[85vh] sm:max-w-[650px]">
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between border-b px-6 py-4">
          <DialogTitle className="text-lg font-semibold">
            {member.name}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: "none" }}>
          {/* Profile */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#F2F5FF] flex items-center justify-center font-semibold text-sm">
              {member.name
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()}
            </div>

            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold">{member.name}</h2>
                <span
                  className={`px-2 py-0.5 text-xs rounded-md ${
                    member.status === "Active"
                      ? "bg-green-100 text-green-600"
                      : member.status === "Invited"
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {member.status}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  {member.email}
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {member.phone}
                </span>
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {member.role}
                </span>
              </div>

              <p className="text-xs text-muted-foreground">
                {t("viewMemberDialog.createdOn", { date: member.createdAt })}
              </p>
            </div>
          </div>

          {/* Associated Groups */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium">{t("viewMemberDialog.associatedGroups")}</h3>
            {loadingGroups ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-1">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("viewMemberDialog.loadingGroups")}
              </div>
            ) : userGroups.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {userGroups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setViewGroup(g)}
                    className="cursor-pointer rounded-md bg-gray-100 px-3 py-1 text-xs text-blue-600 transition hover:bg-gray-200"
                  >
                    {g.name}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">{t("viewMemberDialog.noGroups")}</p>
            )}
          </div>

          {/* Permissions */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">{t("viewMemberDialog.permissions")}</h3>

            {loadingPerms ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {t("viewMemberDialog.loadingPermissions")}
              </div>
            ) : groupedPermissions.length > 0 ? (
              <div className="space-y-3">
                {groupedPermissions.map(({ title, items }) => (
                  <div key={title} className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">
                      {title}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(items ?? []).map((item) =>
                        item.description ? (
                          <Tooltip key={`${title}-${item.label}`}>
                            <TooltipTrigger asChild>
                              <span className="cursor-default px-3 py-1 text-xs bg-gray-100 rounded-md">
                                {item.label}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              {item.description}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span
                            key={`${title}-${item.label}`}
                            className="px-3 py-1 text-xs bg-gray-100 rounded-md"
                          >
                            {item.label}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">{t("viewMemberDialog.noPermissions")}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <ViewGroupMembersDialog
      open={!!viewGroup}
      onOpenChange={(val) => {
        if (!val) setViewGroup(null);
      }}
      group={viewGroup}
    />
    </>
  );
};
